/**
 * Client-side compliance-footer builder for the Phase-1 "Send Test Email"
 * feature.
 *
 * This is intentionally self-contained: it MUST NOT import anything from
 * `supabase/functions/**` or any Hermes/campaign code. It produces an
 * APPROXIMATE footer for layout preview only — the unsubscribe link points at a
 * harmless landing page (`https://usebuildit.com`), NOT the real signed
 * `/api/v1/email-unsubscribe` endpoint, so it suppresses nobody. The dialog that
 * uses this footer shows an explicit notice that the link is inactive.
 *
 * The real server-minted signed footer/unsubscribe is Phase 2 (spec §8b) and is
 * out of scope here.
 */

export interface BuildTestFooterOptions {
  /** Workspace physical mailing address (CAN-SPAM). Optional in template-editor context. */
  physicalAddress?: string;
  /** Sender signature HTML. Optional. */
  signature?: string;
}

/**
 * Returns footer HTML appended to the test email body:
 *   [signature block] + [physical address] + an inert Unsubscribe link.
 *
 * All styles are inline so they survive email-client HTML sanitization.
 */
export function buildTestFooterHtml({
  physicalAddress,
  signature,
}: BuildTestFooterOptions): string {
  const parts: string[] = [];

  if (signature && signature.trim()) {
    parts.push(
      `<div style="margin-top:16px;">${signature}</div>`
    );
  }

  parts.push('<hr style="border:none;border-top:1px solid #e5e7eb;margin:16px 0;" />');

  if (physicalAddress && physicalAddress.trim()) {
    parts.push(
      `<p style="margin:0 0 8px;color:#6b7280;font-size:12px;line-height:1.5;">${physicalAddress}</p>`
    );
  }

  parts.push(
    '<p style="margin:0;color:#6b7280;font-size:12px;line-height:1.5;">' +
      '<a href="https://usebuildit.com" style="color:#6b7280;text-decoration:underline;">Unsubscribe</a>' +
      '</p>'
  );

  return `<div style="margin-top:24px;">${parts.join('')}</div>`;
}
